/**
 * Contraste dos tokens de cor: os pares que a interface de fato junta, os
 * temas em que eles valem e a razão da WCAG 2.1.
 *
 * Funções PURAS, como as de `lerTokens.ts`: recebem o texto de `tokens.css` e
 * devolvem números. Servem ao script de auditoria (`scripts/auditar-contraste.ts`,
 * que roda no Node antes do build e derruba a construção) e à documentação viva
 * (`/admin/design-system`, que lê `tokens.css?raw`). Os dois medem com a MESMA
 * lista e a MESMA fórmula: a razão que a documentação mostra é a que o build
 * confere, e nenhuma das duas é escrita à mão.
 *
 *   4,5:1  texto normal
 *   3:1    texto grande e elemento de interface (borda, foco, série de gráfico)
 */
import { lerBloco, paraRgb, type Hsl, type Rgb } from './lerTokens';

/** RGB (0..1) -> luminância relativa (fórmula WCAG). */
export function luminancia([r, g, b]: Rgb): number {
  const linear = (canal: number): number =>
    canal <= 0.03928 ? canal / 12.92 : ((canal + 0.055) / 1.055) ** 2.4;
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** Razão de contraste entre duas cores em RGB (0..1). */
export function razaoRgb(a: Rgb, b: Rgb): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Razão de contraste entre duas cores HSL, como estão em tokens.css. */
export function razao(a: Hsl, b: Hsl): number {
  return razaoRgb(paraRgb(a), paraRgb(b));
}

/**
 * Compõe `cor` com opacidade `alfa` sobre BRANCO.
 *
 * Existe para o véu do hero, que é translúcido sobre uma fotografia: o pior
 * caso de contraste não é o token puro, é o token deixando a foto atravessar.
 * Branco é o limite superior de qualquer foto, então é o pior caso honesto —
 * e não depende de qual imagem a tela passar ao componente.
 */
export function sobreBranco(cor: Hsl, alfa: number): Rgb {
  const rgb = paraRgb(cor);
  return [alfa * rgb[0] + (1 - alfa), alfa * rgb[1] + (1 - alfa), alfa * rgb[2] + (1 - alfa)];
}

/** Par frente/fundo a conferir e o mínimo exigido. */
export interface Par {
  frente: string;
  fundo: string;
  minimo: number;
  descricao: string;
  /**
   * Opacidade do fundo, quando ele é translúcido sobre uma fotografia (o véu
   * do hero). Ausente = fundo opaco, o caso normal.
   */
  alfaDoFundo?: number;
}

/**
 * Texto normal = 4,5:1. Borda, campo e foco (elemento de interface) = 3:1.
 * Só pares que de fato aparecem juntos na tela — comparar cores que nunca se
 * encontram geraria alarme falso.
 */
export const PARES: readonly Par[] = [
  { frente: 'foreground', fundo: 'background', minimo: 4.5, descricao: 'texto sobre o fundo' },
  { frente: 'card-foreground', fundo: 'card', minimo: 4.5, descricao: 'texto no cartão' },
  {
    frente: 'muted-foreground',
    fundo: 'background',
    minimo: 4.5,
    descricao: 'texto secundário sobre o fundo',
  },
  {
    frente: 'muted-foreground',
    fundo: 'muted',
    minimo: 4.5,
    descricao: 'texto secundário sobre --muted',
  },
  {
    frente: 'popover-foreground',
    fundo: 'popover',
    minimo: 4.5,
    descricao: 'texto em menu/popover',
  },
  {
    frente: 'primary-foreground',
    fundo: 'primary',
    minimo: 4.5,
    descricao: 'texto no botão primário',
  },
  {
    frente: 'secondary-foreground',
    fundo: 'secondary',
    minimo: 4.5,
    descricao: 'texto no botão secundário',
  },
  {
    frente: 'accent-foreground',
    fundo: 'accent',
    minimo: 4.5,
    descricao: 'texto sobre o realce',
  },
  {
    frente: 'destructive-foreground',
    fundo: 'destructive',
    minimo: 4.5,
    descricao: 'texto no botão destrutivo e no número do sino de notificações',
  },
  {
    frente: 'success-foreground',
    fundo: 'success',
    minimo: 4.5,
    descricao: 'texto no selo de sucesso',
  },
  {
    frente: 'warning-foreground',
    fundo: 'warning',
    minimo: 4.5,
    descricao: 'texto no selo de atenção',
  },
  {
    frente: 'info-foreground',
    fundo: 'info',
    minimo: 4.5,
    descricao: 'texto no selo de informação',
  },
  {
    frente: 'govbr-foreground',
    fundo: 'govbr',
    minimo: 4.5,
    descricao: 'texto e ícone no botão do gov.br',
  },
  {
    frente: 'sidebar-foreground',
    fundo: 'sidebar-background',
    minimo: 4.5,
    descricao: 'texto na barra lateral',
  },
  {
    frente: 'sidebar-accent-foreground',
    fundo: 'sidebar-accent',
    minimo: 4.5,
    descricao: 'texto no item ativo da barra lateral',
  },
  {
    frente: 'link',
    fundo: 'background',
    minimo: 4.5,
    descricao: 'texto de ligação sobre o fundo',
  },
  {
    frente: 'link',
    fundo: 'card',
    minimo: 4.5,
    descricao: 'texto de ligação no cartão',
  },
  // Situação como texto num cartão (rótulo do checklist de documentos, aviso
  // de erro): os tokens -text, separados do fundo de selo de mesmo nome.
  {
    frente: 'success-text',
    fundo: 'card',
    minimo: 4.5,
    descricao: 'texto de sucesso no cartão',
  },
  {
    frente: 'warning-text',
    fundo: 'card',
    minimo: 4.5,
    descricao: 'texto de atenção no cartão',
  },
  {
    frente: 'destructive-text',
    fundo: 'card',
    minimo: 4.5,
    descricao: 'texto de erro no cartão, incluindo a mensagem de erro dos campos',
  },
  {
    frente: 'destructive-text',
    fundo: 'background',
    minimo: 4.5,
    descricao: 'texto de erro sobre o fundo, incluindo a mensagem de erro dos campos',
  },
  // Véu do hero do portal (PortalHero, PortalInicio): --hero-overlay num
  // degradê de 95% a 70% por cima da fotografia. O ponto mais fraco do degradê
  // é o que decide, e a foto por baixo é conferida no seu limite claro
  // (branco), para o resultado não depender de qual imagem a tela usa.
  {
    frente: 'hero-overlay-foreground',
    fundo: 'hero-overlay',
    alfaDoFundo: 0.7,
    minimo: 4.5,
    descricao: 'texto no véu do hero, no ponto mais fraco do degradê',
  },
  {
    frente: 'hero-overlay-foreground',
    fundo: 'hero-overlay',
    alfaDoFundo: 0.95,
    minimo: 4.5,
    descricao: 'texto no véu do hero, no ponto mais forte do degradê',
  },
  // Elemento de interface: 3:1. Aqui entra o anel de FOCO, que a WCAG 2.4.11 e
  // 1.4.11 exigem visível — é o único indicador de onde o teclado está.
  //
  // `border` e `input` NÃO entram: a WCAG 1.4.11 isenta separador decorativo e
  // limite de componente que se identifica por outro meio (o cartão pelo
  // fundo, o campo pelo rótulo e pela área de clique). Exigir 3:1 de toda borda
  // deixaria a interface com traço cinza pesado em tudo, o que não é o padrão
  // institucional validado. O foco, esse, é obrigatório.
  { frente: 'ring', fundo: 'background', minimo: 3, descricao: 'anel de foco sobre o fundo' },
  { frente: 'ring', fundo: 'card', minimo: 3, descricao: 'anel de foco sobre o cartão' },
  // Séries de gráfico (ui/charts/chartTheme.tsx): componente gráfico, 3:1 sobre
  // o cartão em que o gráfico mora (WCAG 1.4.11). A fatia vizinha não entra:
  // o PieChartCard separa as fatias com um traço de --card.
  ...[1, 2, 3, 4, 5, 6].map((n): Par => ({
    frente: `chart-${n}`,
    fundo: 'card',
    minimo: 3,
    descricao: `série ${n} de gráfico sobre o cartão`,
  })),
];

/**
 * Pares que só existem no alto contraste. `index.css` pinta TODA ligação com
 * `--link` nesse modo (`html.a11y-high-contrast a`), por cima da cor que o
 * componente escolheu — então o item de navegação ativo, que é um `<a>` sobre
 * `--sidebar-accent` (cabeçalho do portal, barra lateral interna), mostra
 * `--link`, não `--sidebar-accent-foreground`.
 */
export const PARES_ALTO_CONTRASTE: readonly Par[] = [
  {
    frente: 'link',
    fundo: 'sidebar-accent',
    minimo: 4.5,
    descricao: 'ligação no item ativo do cabeçalho e da barra lateral',
  },
  {
    frente: 'link',
    fundo: 'sidebar-background',
    minimo: 4.5,
    descricao: 'ligação no cabeçalho e na barra lateral',
  },
];

/** Um tema da auditoria: os blocos de tokens.css que ele empilha e os pares que confere. */
export interface Tema {
  nome: string;
  blocos: readonly string[];
  pares: readonly Par[];
}

/**
 * `blocos` em ordem de precedência: o primeiro que define o token vence. O alto
 * contraste é uma classe a mais no `<html>`, por cima do tema claro ou do
 * escuro — o token que ele não redefine vem do tema de baixo, então ele é
 * conferido sobre os dois.
 */
export const TEMAS: readonly Tema[] = [
  { nome: 'claro', blocos: [':root'], pares: PARES },
  { nome: 'escuro', blocos: ['.dark', ':root'], pares: PARES },
  {
    nome: 'alto contraste sobre o claro',
    blocos: ['html.a11y-high-contrast', ':root'],
    pares: [...PARES, ...PARES_ALTO_CONTRASTE],
  },
  {
    nome: 'alto contraste sobre o escuro',
    blocos: ['html.a11y-high-contrast', '.dark', ':root'],
    pares: [...PARES, ...PARES_ALTO_CONTRASTE],
  },
];

/** Valor de cada token no tema: o primeiro bloco que o define vence. */
export function tokensDoTema(css: string, tema: Tema): Map<string, Hsl> {
  const resolvidos = new Map<string, Hsl>();
  // Do bloco de baixo para o de cima, para o de cima sobrescrever.
  for (const seletor of [...tema.blocos].reverse()) {
    for (const [nome, cor] of lerBloco(css, seletor)) {
      resolvidos.set(nome, cor);
    }
  }
  return resolvidos;
}

/**
 * Razão de um par com os tokens já resolvidos do tema, ou `undefined` se um dos
 * dois tokens não existir nele.
 */
export function razaoDoPar(par: Par, tokens: ReadonlyMap<string, Hsl>): number | undefined {
  const frente = tokens.get(par.frente);
  const fundo = tokens.get(par.fundo);
  if (!frente || !fundo) {
    return undefined;
  }
  return par.alfaDoFundo === undefined
    ? razao(frente, fundo)
    : razaoRgb(paraRgb(frente), sobreBranco(fundo, par.alfaDoFundo));
}

/** Resultado da auditoria de `tokens.css` inteiro. */
export interface ResultadoDaAuditoria {
  /** Pares medidos (os de token ausente não contam). */
  conferidos: number;
  /** Quantos temas foram conferidos. */
  temas: number;
  /** Uma linha por par abaixo do mínimo ou com token ausente, já no texto do relatório. */
  falhas: string[];
}

/** Confere todos os pares de todos os temas. Não imprime nem encerra o processo. */
export function auditarContraste(css: string): ResultadoDaAuditoria {
  const falhas: string[] = [];
  let conferidos = 0;

  for (const tema of TEMAS) {
    const tokens = tokensDoTema(css, tema);
    for (const par of tema.pares) {
      const r = razaoDoPar(par, tokens);
      if (r === undefined) {
        falhas.push(`[${tema.nome}] token ausente: ${par.frente} ou ${par.fundo}`);
        continue;
      }

      conferidos += 1;
      if (r < par.minimo) {
        falhas.push(
          `[${tema.nome}] ${par.descricao} (${par.frente}/${par.fundo}): ` +
            `${r.toFixed(2)}:1 — mínimo ${par.minimo}:1`,
        );
      }
    }
  }

  return { conferidos, temas: TEMAS.length, falhas };
}

/** "12,02:1" — duas casas, vírgula decimal, como a documentação mostra. */
export function formatarRazao(r: number): string {
  return `${r.toFixed(2).replace('.', ',')}:1`;
}

/** Cor HSL no formato de tokens.css: "38 92% 33%". */
export function formatarHsl({ h, s, l }: Hsl): string {
  return `${h} ${s}% ${l}%`;
}

/**
 * Lê uma cor digitada no formato de tokens.css ("213 61% 23%", com ou sem
 * `hsl(...)` e vírgulas). Devolve `null` se não for uma cor HSL válida — quem
 * chama mostra o erro, em vez de medir uma cor que ninguém digitou.
 */
export function lerHsl(texto: string): Hsl | null {
  const achado = /^\s*(?:hsl\(\s*)?([\d.]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%\s*\)?\s*$/i.exec(
    texto,
  );
  if (!achado) {
    return null;
  }
  const h = Number(achado[1]);
  const s = Number(achado[2]);
  const l = Number(achado[3]);
  if (![h, s, l].every(Number.isFinite) || h >= 360 || s > 100 || l > 100) {
    return null;
  }
  return { h, s, l };
}
